import React, {Fragment, useCallback, useEffect, useState} from "react";
import {
  Button,
  ButtonToolbar,
  Form,
  IconButton,
  Loader,
  Modal,
  Panel,
  Steps,
  Dropdown,
  Message,
  Popover,
  Whisper
} from "rsuite";
import MoreIcon from '@rsuite/icons/legacy/More';
import PlusIcon from '@rsuite/icons/Plus';
import {confirm} from '@rsuite/interactions';
import AdminComponent from "../../../../auth/AdminComponent/AdminComponent";
import {ExternalDevicesApi} from "../../../../../services/ExternalDevices.api";
import {AddExternalDevicesWidgetProps, ConnectedDevice, SelectedDeviceInfo, SystemDevices} from "./AddExternalDevicesWidget.model";
import {ExternalDeviceStatus} from "../../ExternalDevices.model";
import "./AddExternalDevicesWidget.scss";

type AddExternalDevicesErrorBoundaryState = {
  error: Error | null;
};

class AddExternalDevicesErrorBoundary extends React.Component<
  React.PropsWithChildren,
  AddExternalDevicesErrorBoundaryState
> {
  state: AddExternalDevicesErrorBoundaryState = {error: null};

  static getDerivedStateFromError(error: Error): AddExternalDevicesErrorBoundaryState {
    return {error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Ошибка отображения окна добавления устройств", error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <Message showIcon type="error" header="Ошибка добавления устройств">
          <p>{this.state.error.message || "Неизвестная ошибка отображения"}</p>
          <Button
            type="button"
            appearance="link"
            onClick={() => this.setState({error: null})}
          >
            Повторить
          </Button>
        </Message>
      );
    }

    return this.props.children;
  }
}

const AddExternalDevicesWidget = ({clockStationId}: AddExternalDevicesWidgetProps) => {
  const [loading, setLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<SelectedDeviceInfo | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<ConnectedDevice | null>(null);
  const [address, setAddress] = useState<number | string | null>(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ConnectedDevice[]>([]);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const handleOpen = () => {
    setStep(0)
    setData([])
    setDeviceError(null)
    setSelectedDeviceInfo(null)
    setSelectedDevice(null)
    setAddress(null)
    setLoading(false)
    setOpen(true);
  }
  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setLoading(false)
    setOpen(false);
    setDeviceError(null)
    setSelectedDeviceInfo(null)
    setSelectedDevice(null)
    setAddress(null)
    setStep(-1)
  }

  const fetchConnectedDevices = useCallback(async () => {
    setLoading(true);
    setData([])
    setDeviceError(null)

    try {
      const response: SystemDevices = await ExternalDevicesApi.getExternalDevices(clockStationId)

      const dimmers = (response.dimmers ?? []).map(x => ({address: x.address, type: x.type, typeName: "Диммер", status: x.status, id: x.id}));
      const clocks = (response.clocks ?? []).map(x => ({address: x.address, type: x.type, typeName: "Часы", status: x.status, id: x.id}));
      const electronicClocks = (response.electronicClocks ?? []).map(x => ({address: x.address, type: x.type, typeName: "Электронные часы", status: x.status, id: x.id}));
      const ledPanels = (response.ledPanels ?? []).map(x => ({address: x.address, type: x.type, typeName: "LED-экран", status: x.status, id: x.id}));
      const ups = (response.ups ?? []).map(x => ({address: x.address, type: x.type, typeName: "ИБП", status: x.status, id: x.id}));
      const sensors = (response.sensors ?? []).map(x => ({address: x.address, type: x.type, typeName: "Датчик", status: x.status, id: x.id}));

      setData([
        ...dimmers,
        ...clocks,
        ...ups,
        ...electronicClocks,
        ...ledPanels,
        ...sensors,
      ]);
    } catch (error) {
      console.error("Ошибка получения списка подключенных устройств", error)
      const errorDetails = error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Неизвестная ошибка";

      setDeviceError(
        `Не удалось загрузить список устройств. Попробуйте обновить данные. Причина: ${errorDetails}`
      )
    } finally {
      setLoading(false)
    }
  }, [clockStationId])

  useEffect(() => {
    if (!open) return;

    if (clockStationId === undefined) {
      setLoading(false)
      setDeviceError("Не удалось определить часовую станцию. Закройте окно и попробуйте снова.")
      return;
    }

    fetchConnectedDevices()
  }, [clockStationId, fetchConnectedDevices, open])

  const fetchDeviceInfo = async (address: number | string, type: number | string) => {
    try {
      setLoading(true);
      setDeviceError(null)
      setSelectedDeviceInfo(null)

      const response = await ExternalDevicesApi.getDeviceInfo(clockStationId, address, type)

      setSelectedDeviceInfo(response)
      await setAddress(address)
    } catch (error) {
      console.error(error)
      setDeviceError("Ошибка получения информации по устройству")
    } finally {
      setLoading(false)
    }
  }

  const [step, setStep] = React.useState(0);
  const onChange = (nextStep: number) => {
    setStep(nextStep < 0 ? 0 : nextStep > 3 ? 3 : nextStep);
  };

  const onNext = () => onChange(step + 1);
  const onPrevious = () => onChange(step - 1);
  const selectDevice = async (device: ConnectedDevice) => {
    await setSelectedDevice(device)
    onNext()
    fetchDeviceInfo(device.address, device.type)
  }

  const onAddDevice = async () => {
    try {
      setDeviceError(null)
      setLoading(true)

      const payload = {
        article: selectedDeviceInfo?.article,
        controlBlockId: clockStationId,
        address: selectedDevice?.address,
        type: selectedDevice?.type,
        Model: selectedDeviceInfo?.model,
        serialNumber: selectedDeviceInfo?.model?.toLowerCase().includes("iaura.y")
          ? `Y${selectedDevice?.address?.toString().padStart(4, "0")}`
          : selectedDevice?.address?.toString(),
        place: selectedDeviceInfo?.place,
        fabricDate: selectedDeviceInfo?.fabricDate,
        firmwareVersion: selectedDeviceInfo?.firmwareVersion
      }

      const result = await ExternalDevicesApi.addDevice(payload)

      if (result) {
        onNext()
      }
    } catch (error) {
      console.error(error)
      setDeviceError("Произошла ошибка при добавлении устройства")
    } finally {
      setLoading(false)
    }
  }

  const onAddressUpdate = async () => {
    const options = {okButtonText: 'Да', cancelButtonText: 'Нет'};
    const result = await confirm('Вы уверены, что хотите установить новый адрес', options);

    if (result) {
      if (!selectedDevice || address == null || address === '') return;
      await updateAddress(selectedDevice.type, selectedDevice.address, address);
    }
  }

  const updateAddress = async (type: number | string, oldAddress: number | string, newAddress: number | string) => {
    try {
      await ExternalDevicesApi.updateAddress(
        clockStationId,
        type,
        oldAddress,
        newAddress,
      );
    } catch (error) {
      console.error(error)
      setDeviceError("Произошла ошибка при смене адреса устройства")
    }
  }

  const onRemoveDevice = async (deviceId: number | string) => {
    try {
      setRemoveLoading(true)

      await ExternalDevicesApi.deleteDevice(String(deviceId));

      fetchConnectedDevices()

    } catch (error) {
      console.error(error)
      setDeviceError("Произошла ошибка при добавлении устройства")
    } finally {
      setRemoveLoading(false)
    }
  }

  const renderMenu = ({onClose, left, top, className}: any, ref: any, rowData: ConnectedDevice) => {
    return (
      <Popover ref={ref} className={className} style={{left, top}} full>
        <Dropdown.Menu>
          <Dropdown.Item eventKey={1}>
            <Button appearance={"ghost"}
                    loading={removeLoading}
                    color={"red"}
                    onClick={() => onRemoveDevice(rowData.id)}>Удалить</Button>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };

  const renderDeviceAction = (device: ConnectedDevice) => {
    if (device.status === ExternalDeviceStatus.New) {
      return (
        <Button appearance="link" onClick={() => selectDevice(device)}>
          Добавить
        </Button>
      );
    }

    return (
      <Whisper
        placement="autoVerticalEnd"
        trigger="click"
        speaker={(handlers, ref) => renderMenu(handlers, ref, device)}
      >
        <IconButton appearance="subtle" icon={<MoreIcon/>}/>
      </Whisper>
    );
  };

  return (
    <Fragment>
      <Modal
        backdrop={"static"}
        className="add-external-devices-modal"
        keyboard={false}
        open={open}
        onClose={handleClose}
        overflow={false}
      >
        <Modal.Header>
          <Modal.Title>Добавление устройств</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Steps current={step}>
            <Steps.Item/>
            <Steps.Item/>
            <Steps.Item/>
          </Steps>
          <hr/>
          <Panel header={"Список устройств"} hidden={step !== 0}>
            {deviceError && (
              <Message showIcon type="error" header="Ошибка">
                {deviceError}
              </Message>
            )}
            <div className="add-external-devices-table-wrapper">
              {loading && <Loader center backdrop content="Loading..."/>}
              <table className="add-external-devices-table">
                <thead>
                  <tr>
                    <th scope="col">Тип</th>
                    <th scope="col">Адрес</th>
                    <th scope="col" className="add-external-devices-table__actions">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((device, index) => (
                    <tr key={`${device.id}-${device.type}-${device.address}-${index}`}>
                      <td>{device.typeName}</td>
                      <td>{device.address}</td>
                      <td className="add-external-devices-table__actions">
                        {renderDeviceAction(device)}
                      </td>
                    </tr>
                  ))}
                  {!loading && !deviceError && data.length === 0 && (
                    <tr>
                      <td colSpan={3} className="add-external-devices-table__empty">
                        Устройства не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel header={"Информация об устройстве"} hidden={step !== 1}>
            <Form>
              <Form.Group controlId="article">
                <Form.ControlLabel>Aртикул</Form.ControlLabel>
                <Form.Control name="name" value={selectedDeviceInfo?.article ?? ""} onChange={(value: string) => {
                  const newData = structuredClone(selectedDeviceInfo as SelectedDeviceInfo);
                  newData.article = value
                  setSelectedDeviceInfo(newData)
                }}
                />
              </Form.Group>
              <Form.Group controlId="model">
                <Form.ControlLabel>Модель</Form.ControlLabel>
                <Form.Control name="name" value={selectedDeviceInfo?.model ?? ""} onChange={(value: string) => {
                  const newData = structuredClone(selectedDeviceInfo as SelectedDeviceInfo);
                  newData.model = value
                  setSelectedDeviceInfo(newData)
                }}
                />
              </Form.Group>
              <Form.Group controlId="place">
                <Form.ControlLabel>Место установки</Form.ControlLabel>
                <Form.Control rows={2} value={selectedDeviceInfo?.place ?? ""} name="textarea" onChange={(value: string) => {
                  const newData = structuredClone(selectedDeviceInfo as SelectedDeviceInfo);
                  newData.place = value
                  setSelectedDeviceInfo(newData)
                }}
                />
              </Form.Group>
              <Form.Group controlId="fabricDate">
                <Form.ControlLabel>Дата изготовления</Form.ControlLabel>
                <Form.Control value={selectedDeviceInfo?.fabricDate ?? ""} name="fabricDate" onChange={(value: string) => {
                  const newData = structuredClone(selectedDeviceInfo as SelectedDeviceInfo);
                  newData.fabricDate = value
                  setSelectedDeviceInfo(newData)
                }}
                />
              </Form.Group>
              <Form.Group controlId="firmwareVersion">
                <Form.ControlLabel>Версия прошивки</Form.ControlLabel>
                <Form.Control value={selectedDeviceInfo?.firmwareVersion ?? ""} name="firmwareVersion"/>
              </Form.Group>
              <Form.Group controlId="address">
                <Form.ControlLabel>Адрес</Form.ControlLabel>
                <Form.Control
                  name="address"
                  value={address ?? ""}
                  onChange={(value: string | number | null) => {
                    if (value === '' || value === null || value === undefined) {
                      setAddress('');
                      return;
                    }

                    const num = Number(value);
                    if (!isNaN(num)) {
                      setAddress(num);
                    } else {
                      console.warn('Введено нечисловое значение:', value);
                    }
                  }
                }/>
              </Form.Group>
              <AdminComponent>
                {
                  () => (
                    <Form.Group>
                      <Button onClick={onAddressUpdate}
                              appearance={"subtle"}
                              color={"orange"}
                              disabled={loading}>
                        Установить новый адрес
                      </Button>
                    </Form.Group>
                  )
                }
              </AdminComponent>
              <Form.Group>
                <ButtonToolbar>
                  <Button
                    onClick={() => {
                      if (!selectedDevice) return;
                      fetchDeviceInfo(selectedDevice.address, selectedDevice.type).then();
                    }}
                    disabled={loading}
                  >
                    Обновить информацию
                  </Button>
                  <Button onClick={onAddDevice} appearance={"primary"} color={"green"}>
                    Добавить
                  </Button>
                </ButtonToolbar>
              </Form.Group>
              {
                deviceError &&
                <Message showIcon type="error" header="Ошибка">
                  {deviceError}
                </Message>
              }
            </Form>

            {loading && <Loader center content="loading" backdrop />}
          </Panel>
          <Panel hidden={step !== 2}>
            <Message type="success" centered showIcon header="Устройство успешно добавлено!">
              <p>
              </p>
            </Message>
          </Panel>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={onPrevious} hidden={step === 0 || step === 2} disabled={loading}>
            Назад
          </Button>
          <Button onClick={handleClose} hidden={step !== 2} appearance="primary">
            Ok
          </Button>
          <Button onClick={handleClose} hidden={step === 2} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>

      <IconButton circle icon={<PlusIcon/>} appearance="default" onClick={handleOpen}/>
    </Fragment>
  )
}

const SafeAddExternalDevicesWidget = (props: AddExternalDevicesWidgetProps) => (
  <AddExternalDevicesErrorBoundary>
    <AddExternalDevicesWidget {...props}/>
  </AddExternalDevicesErrorBoundary>
);

export default SafeAddExternalDevicesWidget;
